import React, { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ApolloError } from '@apollo/client'
import { makeStyles } from 'tss-react/mui'
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material'
import messages from '@src/routes/SearchResults/messages'
import { SubstanceProductFieldsFragment } from '@src/queries/ProductSearchQuery.generated'
import ShowMoreLessButton from '@src/components/ShowMoreLessButton'
import { AemAdditionalColumns } from '@src/aem-content/components/DynamicProductTable'
import Product from './Product'
import { useCookies } from '@src/utils/cookies'
import { sendNavigationInteractionEvent } from '@src/utils/analytics'
import {
  EventActionComponentType,
  EventActionElementType,
  EventActionType,
} from '@src/utils/analytics/enums'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import getConfig from 'next/config'

const {
  publicRuntimeConfig: { featureFlags },
} = getConfig()

type KeyAttributesProps = {
  keyAttributes: SubstanceProductFieldsFragment['keyAttributes']
  txnBadgeColAdjustments: boolean | undefined
}

export interface ProductTableProps {
  products: SubstanceProductFieldsFragment[]
  displayBadgesOnProduct: boolean
  focus: string
  error?: ApolloError
  maxRows?: number // aem prop
  isShowImage?: boolean // aem props
  isShowBrand?: boolean // aem props
  isStaticContent?: boolean // aem props
  additionalColumns?: AemAdditionalColumns[] // aem props
  substanceName?: string
  displayViewLink?: boolean
  substanceId?: string
  substancePosition?: number
  substanceKeyAttributes?:
    | SubstanceProductFieldsFragment['keyAttributes']
    | null
}

const useStyles = makeStyles()((theme) => ({
  tContainer: {
    overflowX: 'visible',
  },
  tHeadRow: {
    whiteSpace: 'nowrap',
    '& .MuiTableCell-head': {
      padding: '0 .6rem',
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 600,
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
  },
  tHeadProductNumber: {
    width: 124,
  },
  tHeadSds: {
    width: 52,
    '&:empty': {
      width: 0,
    },
  },
  hideBelowMd: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  utilityRow: {
    display: 'flex',
    marginTop: theme.spacing(4),
    '&:empty': {
      display: 'none',
    },
  },
  showMoreLessButton: {
    flexShrink: 0,
  },
  compressWidth: {
    width: 0,
  },
}))

export const cardCategoryKeys = {
  ANTIBODY: 'ANTIBODY',
  SYRINGE_FILTERS: 'SYRINGE_FILTERS',
}

export const cardCategories = [
  cardCategoryKeys.ANTIBODY,
  cardCategoryKeys.SYRINGE_FILTERS,
]

const PROMO_MAX_ATTRIBUTES = 2
const TXN_BADGE_TYPE = 'MAX_DISCOUNT'
const TXN_BADGE_ORGID = '7950'

const SdsHeaders = ({ showSdsColumn }) => {
  const { classes } = useStyles()
  const { formatMessage } = useIntl()
  return (
    <TableCell className={classes.tHeadSds}>
      {showSdsColumn && formatMessage(messages.SDS)}
    </TableCell>
  )
}

const PricingHeader = () => {
  const { classes } = useStyles()
  const { formatMessage } = useIntl()
  return (
    <TableCell className={classes.compressWidth}>
      {formatMessage(messages.PRICING)}
    </TableCell>
  )
}

const AntibodyHeaders = ({ txnBadgeColAdjustments }) => {
  const { formatMessage } = useIntl()
  return (
    <>
      <TableCell>{formatMessage(messages.CLONALITY)}</TableCell>
      <TableCell>{formatMessage(messages.APPLICATION)}</TableCell>

      {!txnBadgeColAdjustments && (
        <>
          <TableCell>
            {formatMessage({
              id: 'SPECIES_REACTIVITY',
              defaultMessage: 'Species Reactivity',
            })}
          </TableCell>
          <TableCell>{formatMessage(messages.CITATIONS)}</TableCell>
        </>
      )}
    </>
  )
}

const SyringeFilterHeaders = ({ txnBadgeColAdjustments }) => {
  const { formatMessage } = useIntl()
  return (
    <>
      <TableCell>{formatMessage(messages.PORE_SIZE)}</TableCell>
      <TableCell>{formatMessage(messages.DIAMETER)}</TableCell>
      {!txnBadgeColAdjustments && (
        <>
          <TableCell>{formatMessage(messages.STERILITY)}</TableCell>
          <TableCell>{formatMessage(messages.MATERIAL)}</TableCell>
        </>
      )}
    </>
  )
}

/**
 * Function for generating the dynamic ID's for the
 * formatting of messages for translations
 * @param string the key attribute to generate the id for
 * @returns the created id for formatting the message
 * Pore Size -> KEY_ATTRIBUTE_PORE_SIZE e.g
 */
const generateKeyAttributeId = (string: string): string =>
  string
    .toUpperCase() // Converting the string to uppercase for Format ID
    .replace(/[^A-Z0-9]+/g, '_') // Replace non-alphanumeric characters with underscores
    .replace(/_+$/g, '') // Remove trailing underscores if any

const KeyAttributesHeaders = ({
  keyAttributes,
  txnBadgeColAdjustments,
}: KeyAttributesProps) => {
  const { formatMessage } = useIntl()

  if (keyAttributes?.length) {
    const maxAttributes = txnBadgeColAdjustments
      ? PROMO_MAX_ATTRIBUTES
      : keyAttributes?.length

    return (
      <>
        {keyAttributes?.slice(0, maxAttributes).map(({ key, label }) => {
          // Camel Casing the label from the API response
          const cellValue = label.replace(/\b\w/g, (char: string) =>
            char.toUpperCase()
          )
          const formattedMessageId = generateKeyAttributeId(cellValue)

          return (
            <TableCell key={`${key}_${label}`}>
              {formattedMessageId ? (
                <FormattedMessage
                  id={formattedMessageId}
                  defaultMessage={cellValue}
                />
              ) : (
                cellValue
              )}
            </TableCell>
          )
        })}
      </>
    )
  }

  return <TableCell>{formatMessage(messages.DESCRIPTION)}</TableCell>
}

const ProductHeaders = ({
  additionalColumns,
  isShowBrand,
  showDescriptionColumn,
}): JSX.Element => {
  const { formatMessage } = useIntl()

  return (
    <>
      {showDescriptionColumn && (
        <TableCell>{formatMessage(messages.DESCRIPTION)}</TableCell>
      )}
      {isShowBrand && <TableCell>{formatMessage(messages.BRAND)}</TableCell>}
      {additionalColumns &&
        additionalColumns.map(({ text }) => (
          <TableCell key={text} data-additional="true">
            {text}
          </TableCell>
        ))}
    </>
  )
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  displayBadgesOnProduct,
  focus,
  maxRows,
  isShowImage = false,
  isShowBrand = false,
  isStaticContent = false,
  additionalColumns,
  displayViewLink,
  substanceId,
  substancePosition,
  substanceKeyAttributes,
}) => {
  const { classes, cx } = useStyles()
  const [cookies] = useCookies(['language'])
  const [expanded, setExpanded] = useState(false)
  const limit = maxRows || 5
  const maxProductsToShow = expanded ? products.length : limit
  const { formatMessage } = useIntl()
  const productIsAntibody =
    !!products.length && products[0]?.cardCategory === cardCategoryKeys.ANTIBODY
  const productIsSyringeFilter =
    !!products.length &&
    products[0]?.cardCategory === cardCategoryKeys.SYRINGE_FILTERS
  // STRAT-19275 * hide SDS column if none of the products in the table has an SDS to show
  const showSdsColumn =
    products.filter((p) => p?.sdsLanguages?.length > 0).length > 0

  const isEnglish = cookies.language.toLowerCase() === 'en'

  const { currentUser, isB2BUser } = useCurrentUser()
  const salesOrg: string = useMemo(() => {
    return (
      currentUser?.soldToPartners?.[0]?.salesOrg ||
      currentUser?.metadata.salesOrg ||
      ''
    )
  }, [currentUser])
  const country: string = useMemo(() => {
    return currentUser?.metadata?.country || ''
  }, [currentUser])

  const productHasTxnPromo = products.some(
    (product) =>
      product?.txnBadge?.type === TXN_BADGE_TYPE &&
      product?.txnBadge?.salesOrgIds?.includes(TXN_BADGE_ORGID)
  )

  // FIND-1427 adjust number of columns displayed with promo badge
  const txnBadgeColAdjustments =
    featureFlags.srpTxnBadges &&
    country === 'US' &&
    !isB2BUser &&
    salesOrg === TXN_BADGE_ORGID &&
    productHasTxnPromo

  const AltTableHeaders = productIsAntibody
    ? AntibodyHeaders
    : productIsSyringeFilter
      ? SyringeFilterHeaders
      : null

  const showKeyAttributeHeaders =
    !AltTableHeaders &&
    isEnglish &&
    substanceKeyAttributes &&
    substanceKeyAttributes.length > 0

  const showDescription =
    (!showKeyAttributeHeaders && !AltTableHeaders) || !isEnglish

  const handleShowMoreLessClick = () => {
    const actionType = expanded ? 'show less' : 'show more'
    sendNavigationInteractionEvent({
      action: EventActionType.InPageClick,
      detail: `${actionType} products | ${substanceId}`,
      component: EventActionComponentType.Body,
      elementType: EventActionElementType.AnchorLink,
      elementText: actionType,
      productName: 'water',
    })
  }

  return (
    <TableContainer className={classes.tContainer}>
      <Table size="small">
        <TableHead>
          <TableRow className={classes.tHeadRow}>
            {isShowImage ? (
              <TableCell />
            ) : !isStaticContent ? (
              <TableCell
                className={cx(classes.hideBelowMd, classes.compressWidth)}
              >
                {formatMessage(messages.COMPARE)}
              </TableCell>
            ) : null}
            <TableCell classes={{ root: classes.tHeadProductNumber }}>
              {formatMessage(messages.PRODUCT_NO)}
            </TableCell>

            {AltTableHeaders && (
              <AltTableHeaders
                txnBadgeColAdjustments={txnBadgeColAdjustments}
              />
            )}

            {showKeyAttributeHeaders && (
              <KeyAttributesHeaders
                keyAttributes={substanceKeyAttributes}
                txnBadgeColAdjustments={txnBadgeColAdjustments}
              />
            )}

            {!products[0]?.cardCategory && (
              <ProductHeaders
                additionalColumns={additionalColumns}
                isShowBrand={isShowBrand}
                showDescriptionColumn={showDescription}
              />
            )}
            <TableCell colSpan={4} />
            <SdsHeaders showSdsColumn={showSdsColumn} />
            <PricingHeader />
          </TableRow>
        </TableHead>
        <TableBody>
          {products
            .slice(0, maxProductsToShow)
            .map((product, index) =>
              product instanceof Error || !product ? null : (
                <Product
                  additionalColumns={additionalColumns}
                  displayBadgesOnProduct={displayBadgesOnProduct}
                  displayViewLink={displayViewLink}
                  focus={focus}
                  isShowBrand={isShowBrand}
                  isShowImage={isShowImage}
                  isStaticContent={isStaticContent}
                  key={`${product.productKey}${product.brand.erpKey}-${index}`}
                  product={product}
                  productIsAntibody={productIsAntibody}
                  productIsSyringeFilter={productIsSyringeFilter}
                  showDescription={showDescription}
                  substanceId={substanceId}
                  substancePosition={substancePosition}
                  txnBadgeColAdjustments={txnBadgeColAdjustments}
                />
              )
            )}
        </TableBody>
      </Table>
      <div className={classes.utilityRow}>
        {products.length > limit && (
          <ShowMoreLessButton
            expanded={expanded}
            setExpanded={setExpanded}
            className={classes.showMoreLessButton}
            data-testid={`show-${!expanded ? 'more' : 'less'}`}
            onClick={handleShowMoreLessClick}
          />
        )}
      </div>
    </TableContainer>
  )
}

export default ProductTable
